import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  OPEN_SETTINGS_EVENT,
  declineAnalytics,
  isRestrictedRegion,
  readConsent,
  saveConsent,
} from '@/lib/cookieConsent';

const copy = {
  ja: {
    label: 'クッキーの設定',
    body: 'プライバシーを大切にしています。クッキーを受け入れますか？',
    settingsBody: 'この地域では、拒否しない限り分析と広告のクッキーが動作します。',
    accept: '同意する',
    decline: '同意しない',
    privacy: 'プライバシーポリシー',
    close: '閉じる',
  },
  en: {
    label: 'Cookie settings',
    body: 'We respect your privacy. Do you want to accept cookies?',
    settingsBody: 'Analytics and ad cookies run here unless you decline.',
    accept: 'Accept',
    decline: 'Decline',
    privacy: 'Privacy policy',
    close: 'Close',
  },
  ko: {
    label: '쿠키 설정',
    body: '개인정보를 소중히 생각합니다. 쿠키를 허용하시겠습니까?',
    settingsBody: '이 지역에서는 거부하지 않는 한 분석 및 광고 쿠키가 작동합니다.',
    accept: '동의',
    decline: '거부',
    privacy: '개인정보 처리방침',
    close: '닫기',
  },
  zh: {
    label: 'Cookie 设置',
    body: '我们尊重您的隐私。您是否接受 Cookie？',
    settingsBody: '在您所在地区，除非您拒绝，否则分析和广告 Cookie 会运行。',
    accept: '同意',
    decline: '拒绝',
    privacy: '隐私政策',
    close: '关闭',
  },
} as const;

/**
 * Restricted regions (EU, EEA, UK, Switzerland, or an unknown country): a slim opt-in bar
 * until the visitor answers. Everywhere else there is no bar; the footer Cookie settings
 * link opens the same slim bar as an Accept / Decline choice.
 */
export function CookieConsent() {
  const { language } = useLanguage();
  const [mode, setMode] = useState<'hidden' | 'ask' | 'settings'>('hidden');
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    try {
      if (!readConsent()) {
        // Wait briefly so the bar never competes with first paint, and for the region answer.
        const delay = new Promise((r) => setTimeout(r, 1200));
        Promise.all([delay, isRestrictedRegion()]).then(([, restricted]) => {
          if (!cancelled && restricted) setMode('ask');
        });
      }
    } catch {
      /* storage blocked: do not nag */
    }
    const open = () => setMode('settings');
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => {
      cancelled = true;
      window.removeEventListener(OPEN_SETTINGS_EVENT, open);
    };
  }, []);

  const visible = mode !== 'hidden';

  // Reserve room under the page while the bar shows so it never covers content or footer links.
  useEffect(() => {
    if (!visible) return;
    const el = barRef.current;
    if (!el) return;
    const root = document.documentElement;
    const sync = () => root.style.setProperty('--cookie-bar-h', `${el.offsetHeight + 8}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    document.body.style.paddingBottom = 'var(--cookie-bar-h)';
    return () => {
      ro.disconnect();
      root.style.removeProperty('--cookie-bar-h');
      document.body.style.paddingBottom = '';
    };
  }, [visible]);

  const decide = (choice: 'accepted' | 'declined') => {
    try {
      saveConsent(choice);
    } catch {
      /* ignore */
    }
    if (choice === 'accepted') window.__jscLoadTracking?.();
    else declineAnalytics();
    setMode('hidden');
  };

  if (!visible) return null;

  const c = copy[language as keyof typeof copy] ?? copy.en;

  return (
    <div
      ref={barRef}
      role="dialog"
      aria-live="polite"
      aria-label={c.label}
      className="fixed inset-x-0 bottom-0 z-[60] animate-in slide-in-from-bottom duration-300"
    >
      <div className="border-t border-border/60 bg-card/95 px-3 py-1.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto flex max-w-4xl flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
          <p className="flex-1 text-[11px] leading-snug text-muted-foreground sm:text-xs">
            {mode === 'ask' ? c.body : c.settingsBody}
          </p>
          <div className="flex items-center gap-2 sm:shrink-0">
            <Link to={`/${language}/privacy/`} className="mr-auto text-[11px] text-muted-foreground underline hover:text-foreground sm:mr-1 sm:text-xs">
              {c.privacy}
            </Link>
            {mode === 'settings' && (
              <Button variant="ghost" size="sm" className="h-9 px-2 text-xs text-muted-foreground sm:h-8" onClick={() => setMode('hidden')}>
                {c.close}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-9 px-2 text-xs text-muted-foreground sm:h-8" onClick={() => decide('declined')}>
              {c.decline}
            </Button>
            <Button size="sm" className="h-9 px-4 text-xs sm:h-8" onClick={() => decide('accepted')}>
              {c.accept}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
